import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, Check, Tags, Search } from 'lucide-react';
import { useTagsStore } from '@flows.cash/core';
import { Tag } from '@flows.cash/types';
import { useAuthStore } from '@/store/authStore';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { motion, AnimatePresence } from 'framer-motion';

const colorOptions = [
  { id: 'blue', name: 'Blue', value: '#3B82F6' },
  { id: 'indigo', name: 'Indigo', value: '#6366F1' },
  { id: 'purple', name: 'Purple', value: '#8B5CF6' },
  { id: 'pink', name: 'Pink', value: '#EC4899' },
  { id: 'red', name: 'Red', value: '#EF4444' },
  { id: 'orange', name: 'Orange', value: '#F97316' },
  { id: 'amber', name: 'Amber', value: '#F59E0B' },
  { id: 'yellow', name: 'Yellow', value: '#EAB308' },
  { id: 'lime', name: 'Lime', value: '#84CC16' },
  { id: 'green', name: 'Green', value: '#22C55E' },
  { id: 'emerald', name: 'Emerald', value: '#10B981' },
  { id: 'teal', name: 'Teal', value: '#14B8A6' },
  { id: 'cyan', name: 'Cyan', value: '#06B6D4' },
  { id: 'sky', name: 'Sky', value: '#0EA5E9' },
  { id: 'violet', name: 'Violet', value: '#7C3AED' },
  { id: 'fuchsia', name: 'Fuchsia', value: '#D946EF' },
  { id: 'rose', name: 'Rose', value: '#F43F5E' },
  { id: 'slate', name: 'Slate', value: '#64748B' },
];

export default function TagsSettings() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { tags, addTag, updateTag, deleteTag, fetchTags } = useTagsStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    color: colorOptions[0].value
  });

  useEffect(() => {
    if (user?.id) {
      fetchTags(user.id);
    }
  }, [user?.id, fetchTags]);

  const filteredTags = tags.filter(tag =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) {
      toast.error('Please sign in to manage tags');
      return;
    }

    try {
      if (editingTag) {
        await updateTag(editingTag.id, {
          name: formData.name.trim(),
          color: formData.color
        });
        toast.success('Tag updated successfully');
      } else {
        await addTag({
          name: formData.name.trim(),
          color: formData.color,
          user_id: user.id
        });
        toast.success('Tag added successfully');
      }
      
      handleCloseModal();
    } catch (error) {
      console.error('Error saving tag:', error);
      toast.error('Failed to save tag');
    }
  };

  const handleEditTag = (tag: any) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      color: tag.color
    });
    setIsModalOpen(true);
  };

  const handleDeleteTag = async (tagId: string) => {
    if (!user?.id) return;

    try {
      await deleteTag(user.id, tagId);
      toast.success('Tag deleted successfully');
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error('Failed to delete tag');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTag(null);
    setFormData({
      name: '',
      color: colorOptions[0].value
    });
  };

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="text-center py-12"
    >
      <div className="text-text-secondary mb-4">
        <Tags className="w-12 h-12 mx-auto" />
      </div>
      <motion.h3 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent mb-3"
      >
        {tags.length === 0 ? t('settings.tagsSettings.empty.title') : t('settings.tagsSettings.noResults.title')}
      </motion.h3>
      <motion.p 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-text-secondary mb-8 max-w-md mx-auto"
      >
        {tags.length === 0 
          ? t('settings.tagsSettings.empty.description')
          : t('settings.tagsSettings.noResults.description')}
      </motion.p>
      {tags.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            onClick={() => setIsModalOpen(true)}
            className="bg-primary text-white hover:bg-primary/90 shadow-sm h-11 rounded-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('settings.tagsSettings.addFirstTag')}
          </Button>
        </motion.div>
      )}
    </motion.div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col px-2 sm:px-6 py-3 sm:py-4 space-y-4"
    >
      <header className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl shadow-sm sticky top-3 z-10">
        <div className="px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-6">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="p-2 bg-primary/10 rounded-xl">
                <Tags className="w-5 h-5 text-primary dark:text-primary-dark" />
              </div>
              <div className="flex-1">
                <h1 className="text-lg sm:text-2xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
                  {t('settings.tagsSettings.title')}
                </h1>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary line-clamp-2 sm:line-clamp-none">
                  {t('settings.tagsSettings.description')}
                </p>
              </div>
            </div>
            <Button
              onClick={() => setIsModalOpen(true)}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white transition-all duration-200 shadow-sm hover:shadow-md dark:shadow-primary-dark/10 dark:hover:shadow-primary-dark/20 rounded-xl h-11"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('settings.tagsSettings.addTag')}
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary w-4 h-4" />
              <Input
                placeholder={t('settings.tagsSettings.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark rounded-xl h-11"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 hover:bg-muted dark:hover:bg-muted-dark"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto">
        <AnimatePresence mode="wait">
          {filteredTags.length === 0 ? (
            <EmptyState />
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            >
              {filteredTags.map(tag => (
                <motion.div
                  key={tag.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  className="group"
                >
                  <Card className="flex items-center justify-between p-4 h-full bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark hover:shadow-md transition-shadow rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="font-medium text-text-primary dark:text-text-dark-primary">
                        {tag.name}
                      </span>
                    </div>
                    <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEditTag(tag)}
                        className="h-8 w-8 text-primary hover:text-primary hover:bg-primary/10 rounded-lg"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDeleteTag(tag.id)}
                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-error/10 rounded-lg"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
              {editingTag ? t('settings.tagsSettings.editTag') : t('settings.tagsSettings.addTag')}
            </DialogTitle>
            <DialogDescription className="text-text-secondary">
              {t('settings.tagsSettings.modalDescription')}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="tag-name" className="text-text-primary dark:text-text-dark-primary">
                {t('settings.tagsSettings.name')}
              </Label>
              <Input
                id="tag-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('settings.tagsSettings.namePlaceholder')}
                maxLength={50}
                className="h-11 bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark rounded-xl"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-text-primary dark:text-text-dark-primary">
                {t('settings.tagsSettings.color')}
              </Label>
              <div className="grid grid-cols-6 gap-2">
                {colorOptions.map(color => (
                  <button
                    key={color.id}
                    type="button"
                    className="w-full aspect-square rounded-lg border-2 flex items-center justify-center transition-all duration-200"
                    style={{ 
                      backgroundColor: color.value,
                      borderColor: formData.color === color.value ? 'white' : color.value
                    }}
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    title={color.name}
                  >
                    {formData.color === color.value && (
                      <Check className="w-4 h-4 text-white" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseModal}
                className="border-border dark:border-border-dark bg-background-secondary/50 dark:bg-background-dark-secondary/50"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={!formData.name.trim()}
                className="bg-primary hover:bg-primary/90 text-white transition-all duration-200"
              >
                {editingTag ? t('actions.save') : t('actions.add')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}